import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { User } from '@/types/auth.ts';
import authService from '@/services/authService.ts';
import { clearPendingFlow, getPendingFlow, type MvpFlow } from '@/lib/mvpFlow';

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSalesperson: boolean;
  isSalesManager: boolean;
  isSuperUser: boolean;
  /**
   * Heuristic for "trial / unactivated" account: a Salesperson that has not been
   * placed inside any tenant yet (empty Company, no TeamId). This is a runtime
   * derivation — the dedicated Trial role will replace this flag once it ships.
   */
  isTrial: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, password: string, firstName: string, lastName: string, phone: string | undefined, termsAccepted: boolean) => Promise<User>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  acknowledgePrivacy: () => Promise<void>;
  /** MVP: persiste en backend el flujo elegido (no-op si ya hay uno fijado). */
  chooseFlow: (flow: MvpFlow) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const me = await authService.getMe();
      setUser(me);
    } catch {
      setUser(null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      refreshUser().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [refreshUser]);

  // MVP: si el usuario eligió un flujo en la home antes de autenticarse, lo
  // persistimos en backend justo después de login/registro (primera elección
  // gana; el backend ignora la llamada si ya hay flujo fijado).
  const syncPendingFlow = useCallback(async (u: User): Promise<User> => {
    const pending = getPendingFlow();
    if (!pending) return u;
    clearPendingFlow();
    if (u.role === 'SuperUser' || u.chosenFlow) return u;
    try {
      return await authService.setChosenFlow(pending);
    } catch {
      return u; // FlowLock reintentará al entrar al flujo.
    }
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<User> => {
    const response = await authService.login({ email, password });
    localStorage.setItem('accessToken', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    const synced = await syncPendingFlow(response.user);
    setUser(synced);
    return synced;
  }, [syncPendingFlow]);

  const register = useCallback(async (email: string, password: string, firstName: string, lastName: string, phone: string | undefined, termsAccepted: boolean): Promise<User> => {
    const response = await authService.register({ email, password, firstName, lastName, phone: phone || undefined, termsAccepted });
    localStorage.setItem('accessToken', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    const synced = await syncPendingFlow(response.user);
    setUser(synced);
    return synced;
  }, [syncPendingFlow]);

  const chooseFlow = useCallback(async (flow: MvpFlow) => {
    const updated = await authService.setChosenFlow(flow);
    setUser(updated);
  }, []);

  const acknowledgePrivacy = useCallback(async () => {
    const updated = await authService.acknowledgePrivacy();
    setUser(updated);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout(localStorage.getItem('refreshToken'));
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      clearPendingFlow();
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        isSalesperson: user?.role === 'Salesperson',
        isSalesManager: user?.role === 'SalesManager' || user?.role === 'SuperUser',
        isSuperUser: user?.role === 'SuperUser',
        isTrial: user?.role === 'Salesperson' && !user.company && !user.teamId,
        login,
        register,
        logout,
        refreshUser,
        acknowledgePrivacy,
        chooseFlow,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
